// Files a person attaches in web chat, checked before anything is uploaded: only the types LAMP takes, never empty, and
// never larger than the limit the Application API enforces too (MAX_RECEIVED_FILE_BYTES there). A file's type is
// taken from its name when the browser doesn't say, as phones often don't for a spreadsheet.

/** The largest file web chat sends: a named setting, the same as the Application API's. */
export const MAX_FILE_BYTES = 7 * 1024 * 1024;
export const MAX_FILE_MEGABYTES = MAX_FILE_BYTES / (1024 * 1024);

/** The types LAMP takes, by file extension. */
export const FILE_TYPES: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  // A Tally export, to bring the business's data in.
  xml: "application/xml",
};

/** The file picker's `accept`: the extensions and types LAMP takes. */
export const ACCEPT = [
  ...Object.keys(FILE_TYPES).map((extension) => `.${extension}`),
  ...new Set(Object.values(FILE_TYPES)),
].join(",");

export type FileCheck =
  | { readonly ok: true; readonly mediaType: string }
  | { readonly ok: false; readonly reason: "type" | "size" | "empty" };

/** Whether web chat may send a file, and as what type. */
export function checkFile(file: { readonly name: string; readonly type: string; readonly size: number }): FileCheck {
  const extension = file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase();
  const byName = file.name.includes(".") ? FILE_TYPES[extension] : undefined;
  const given = Object.values(FILE_TYPES).includes(file.type) ? file.type : undefined;
  // A CSV may come as text/plain or Excel's own type: its name says what it is.
  const mediaType = given ?? byName;
  if (!mediaType) return { ok: false, reason: "type" };
  if (file.size === 0) return { ok: false, reason: "empty" };
  if (file.size > MAX_FILE_BYTES) return { ok: false, reason: "size" };
  return { ok: true, mediaType };
}

/** A file's bytes, base64-encoded, as the Application API takes them. */
export async function base64Of(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(binary);
}
